import React, { Fragment } from "react";
import { lazyWithRetry } from "../../core/lazy_with_retry";

import { ImageData } from "../../data";
import { GeneralInformation } from "../template_offering_details_base";
import { OfferingDetailsDayPassWithCapacity } from "../template_offering_details_daypass_with_capacity";
import { Text } from "../text";
import { TextLink } from "../text_link";
import { useWerqwiseAnalytics } from "./werqwise_analytics";
import { partnerConfig, testimonials } from "./werqwise_partner_config";

const Theme = lazyWithRetry(() => import("./werqwise_theme"));

const faqs = [
  {
    question: "What are your hours?",
    answer: "We are open from 9 AM to 5:30 PM.",
  },

  {
    question: "Can I cancel my day pass purchase?",
    answer: "Unfortunately, the Office Day Pass is non-refundable",
  },

  {
    question: "Can I change the date of my booking?",
    answer:
      "Yes. Please either call us or reply to the confirmation email and we will take care of it.",
  },

  {
    question: "Can I use the meeting rooms?",
    answer: (
      <Text>
        Meeting rooms will have to be booked separately.{" "}
        <TextLink href="https://partners.flexspace.ai/offerings/werqwise-meeting-rooms">
          You may book in on our website.
        </TextLink>
      </Text>
    ),
  },

  {
    question: "Can I use the printer?",
    answer:
      "Yes, please get in touch with the front desk and we will assist you.",
  },

  {
    question: "Where can I keep and heat my lunch?",
    answer:
      "The kitchen, refrigerator and microwave is available for your use.",
  },
  {
    question: "Do you offer coffee, tea, or water?",
    answer: "We offer freshly brewed coffee.",
  },
];

const werqwiseDailySFDesktopImages: ImageData[] = [
  {
    url: "https://meet-production.s3.us-west-1.amazonaws.com/images/werqwise/werqwise-dayoffice-sf-desktop-1.jpg",
    width: 1200,
    height: 674,
  },
  {
    url: "https://meet-production.s3.us-west-1.amazonaws.com/images/werqwise/werqwise-dayoffice-sf-desktop-2.jpg",
    width: 1200,
    height: 675,
  },
  {
    url: "https://meet-production.s3.us-west-1.amazonaws.com/images/werqwise/werqwise-dayoffice-sf-desktop-3.jpg",
    width: 1200,
    height: 857,
  },
  {
    url: "https://meet-production.s3.us-west-1.amazonaws.com/images/werqwise/werqwise-dayoffice-sf-desktop-4.jpg",
    width: 1200,
    height: 857,
  },
  {
    url: "https://meet-production.s3.us-west-1.amazonaws.com/images/werqwise/werqwise-dayoffice-sf-desktop-5.jpg",
    width: 1200,
    height: 1200,
  },
];

const werqwiseDailySFMobileImages: ImageData[] = werqwiseDailySFDesktopImages;

const werqwiseDailySMDesktopImages: ImageData[] = [
  {
    url: "https://meet-production.s3.us-west-1.amazonaws.com/images/werqwise/werqwise-dayoffice-sm-desktop-1.jpg",
    width: 1200,
    height: 674,
  },
  {
    url: "https://meet-production.s3.us-west-1.amazonaws.com/images/werqwise/werqwise-dayoffice-sm-desktop-2.jpg",
    width: 1200,
    height: 857,
  },
  {
    url: "https://meet-production.s3.us-west-1.amazonaws.com/images/werqwise/werqwise-dayoffice-sm-desktop-3.jpg",
    width: 1200,
    height: 857,
  },
];

const werqwiseDailySMMobileImages: ImageData[] = werqwiseDailySMDesktopImages;

const generalInfo: GeneralInformation = {
  title: `General information and cancellation policy`,
  description: "",
  bulletPoints: [
    {
      point: "Cancellation and Changes",
      text: "You may cancel your reservation 24 hours before the reservation begins.",
    },
    {
      point: "Attendees",
      text: "Your office day pass gives you and your team access to a private office. Be sure to select the right capacity that fits your team.",
    },
    {
      point: "Guests",
      text: "Your day pass gives access to one individual only and is not transferable. A pass is required for each guest. Be sure to select the quantity needed to cover all your guests.",
    },
    {
      point: "Health and safety",
      text: "We've updated our space to help you make better choices. From installing our new temperature gate to adding hand sanitizer fixtures, we have added a variety of different ways for you to stay healthy and clean.",
    },
  ],
};

export function OfferingWerqwiseDayOffice() {
  useWerqwiseAnalytics();

  return (
    <Fragment>
      <Theme />
      <OfferingDetailsDayPassWithCapacity
        showAddress={false}
        showDirections
        getDesktopImagesBasedOnLocation={(location) => {
          if (!location) {
            return werqwiseDailySFDesktopImages;
          }

          if (location.city === "San Mateo") {
            return werqwiseDailySMDesktopImages;
          }

          return werqwiseDailySFDesktopImages;
        }}
        getMobileImagesBasedOnLocation={(location) => {
          if (!location) {
            return werqwiseDailySFMobileImages;
          }

          if (location.city === "San Mateo") {
            return werqwiseDailySMMobileImages;
          }

          return werqwiseDailySFMobileImages;
        }}
        partnerConfig={partnerConfig}
        data={{
          description: `Got work from home fatigue? No more distractions. No more challenges. Work in a fully equipped and furnished private office. Reclaim your productivity today!​ ​Bond with colleagues and rediscover group synergy.`,
          faqs,
          testimonials: testimonials,
          generalInfo,
        }}
      />
    </Fragment>
  );
}
